import { Link } from "react-router-dom";
import { Home, ShoppingBag, ArrowRight, ArrowLeft, HelpCircle, Search } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { L, t } from "../lib/translations";

export default function NotFound() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const isRtl = lang === "he";

  return (
    <div className="min-h-screen pt-14 pb-20" data-testid="not-found-page">
      <div className="max-w-xl mx-auto px-4 py-16 flex flex-col items-center text-center">

        {/* Icon */}
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
          style={{ background: "rgba(225,48,108,0.1)", border: "1px solid rgba(225,48,108,0.2)" }}
        >
          <Search className="w-7 h-7" style={{ color: "#E1306C" }} />
        </div>

        {/* 404 badge */}
        <span
          className="text-[10px] font-black uppercase tracking-[0.3em] px-3 py-1 rounded-full mb-4"
          style={{ background: "rgba(225,48,108,0.1)", color: "#E1306C" }}
        >
          404
        </span>

        {/* Heading */}
        <h1 className="text-2xl font-black font-outfit mb-3" style={{ color: "var(--text-primary)" }}>
          {t("NotFound.page_not_found", lang)}
        </h1>
        <p className="text-sm leading-6 max-w-sm" style={{ color: "var(--text-secondary)" }}>
          {t("NotFound.the_page_you_re_looking_for_doesn_t_exist_or_has", lang)}
        </p>

        {/* Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
          <Link
            to="/"
            className="h-10 px-4 rounded-xl font-semibold text-sm inline-flex items-center gap-2 text-white transition-opacity hover:opacity-90"
            style={{ background: "#8B5CF6" }}
          >
            <Home className="w-4 h-4" />
            {t("nav.home", lang)}
          </Link>
          <Link
            to="/shop"
            className="h-10 px-4 rounded-xl font-semibold text-sm inline-flex items-center gap-2 transition-colors hover:bg-white/5"
            style={{ border: "1px solid var(--border-color)", color: "var(--text-primary)" }}
          >
            <ShoppingBag className="w-4 h-4" />
            {t("nav.shop", lang)}
          </Link>
          <Link
            to={user ? "/dashboard" : "/login"}
            className="h-10 px-4 rounded-xl font-semibold text-sm inline-flex items-center gap-2 transition-colors hover:bg-white/5"
            style={{ border: "1px solid var(--border-color)", color: "var(--text-primary)" }}
          >
            {isRtl ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
            {user
              ? t("NotFound.my_account", lang)
              : t("NotFound.sign_in", lang)}
          </Link>
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10 w-full text-start">
          {[
            {
              color: "#F77737",
              label: t("NotFound.quick_tip", lang),
              text: t("NotFound.if_you_followed_an_old_link_the_destination_may_", lang),
            },
            {
              color: "#8B5CF6",
              label: t("NotFound.most_visited", lang),
              text: t("NotFound.most_users_continue_to_the_products_page_or_thei", lang),
            },
            {
              color: "#405DE6",
              icon: <HelpCircle className="w-3 h-3" />,
              label: t("NotFound.need_help", lang),
              text: t("NotFound.open_support_from_the_widget_at_any_time_if_some", lang),
            },
          ].map((card, i) => (
            <div key={i} className="rounded-2xl p-4 border" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
              <div
                className="text-[10px] font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5"
                style={{ color: card.color }}
              >
                {card.icon}
                {card.label}
              </div>
              <p className="text-xs leading-5" style={{ color: "var(--text-secondary)" }}>
                {card.text}
              </p>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
